import { useEffect, useCallback } from 'react';

// GA4 Property ID
const GA4_ID = 'G-BTM4DQQMY4';

// ----------------------------------------------------------------------

/**
 * Google Tag Manager / GA4 Component
 *
 * Features:
 * - Production-only loading
 * - CookieYes consent integration
 * - Enhanced measurement configuration
 * - Debug mode in development
 */
const GoogleTagManager = () => {
  const initializeGA = useCallback(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    // Skip if already initialized
    if (window.gtag) return;

    // Create dataLayer if not exists
    window.dataLayer = window.dataLayer || [];

    // Define gtag function
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag as typeof window.gtag;

    // Initialize with timestamp
    gtag('js', new Date());

    // Configure GA4 with enhanced settings
    gtag('config', GA4_ID, {
      // Enhanced measurement
      send_page_view: true,
      // Anonymize IP for GDPR
      anonymize_ip: true,
      // Cookie settings
      cookie_flags: 'SameSite=None;Secure',
      // Custom dimensions (optional)
      custom_map: {
        dimension1: 'cta_name',
        dimension2: 'section_name',
      },
    });

    // Debug in development
    if (import.meta.env.DEV) {
      console.log('[GA4] Initialized:', GA4_ID);
    }
  }, []);

  useEffect(() => {
    // Only load in production
    if (import.meta.env.VITE_VERCEL_ENV !== 'production') {
      // In development, still initialize gtag for testing
      if (import.meta.env.DEV) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(...args: unknown[]) {
          console.log('[GA4 Dev]', ...args);
          window.dataLayer.push(args);
        } as typeof window.gtag;
      }
      return;
    }

    // Load GTM script asynchronously
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;

    // Initialize after script loads
    gtmScript.onload = initializeGA;

    // Handle errors
    gtmScript.onerror = () => {
      console.warn('[GA4] Failed to load analytics script');
    };

    document.head.appendChild(gtmScript);

    // Cleanup on unmount
    return () => {
      // Script remains in DOM (browser handles caching)
    };
  }, [initializeGA]);

  return null;
};

export default GoogleTagManager;

// ----------------------------------------------------------------------

// Type declarations for global scope
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (
      command: 'event' | 'config' | 'set' | 'js',
      targetIdOrDate: string | Date,
      config?: Record<string, unknown>
    ) => void;
  }
}
